import React, { useContext, useEffect, useState } from 'react';
import { graphql, PageProps } from 'gatsby';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import { ApolloError } from '@apollo/client';
import { userContext } from '../../../gatsby-theme-engagement/src/context';
import { withPrivateRoute } from '../../../gatsby-theme-engagement/src/components/molecules';
import useConfirmSubComms from '../hooks/mutation/useConfirmSubComms';
import useListUserPreferences from '../hooks/queries/useListUserPreferences';
import { getPreferenceByType } from '../components/accountOverview/consentPreferencesUtils';
import { PREFERENCE_TYPE_EMAIL_MARKETING } from '../components/accountOverview/constants';
import { addErrorInDatadogRum } from '../utils/utilities';
import Layout from '../components/common/layout';
import DoubleOptIn from '../components/doubleOptIn';
import ProcessingMsg from '../components/common/ProcessingMsg/ProcessingMsg';

// Backend reports this only for a genuinely invalid/expired ticket; a re-clicked
// (already-consumed) ticket resolves as success instead. LPB-1573.
const INVALID_TICKET_ERROR_TYPE = 'INVALID_TICKET';

const DoubleOptInTemplate = ({
  data: {
    prismicDoubleOptInPage: { lang, data: doubleOptInPageData },
    prismicSignUpLayout: { data: layout },
  },
}: PageProps<{
  prismicDoubleOptInPage: Queries.PrismicDoubleOptInPage;
  prismicSignUpLayout: Queries.PrismicSignUpLayout;
}>) => {
  const { subCommsConfirmed, refetchUser } = useContext(userContext);
  const { rtDoubleOptIn } = useFlags();

  const { data: preferencesData, loading: preferencesLoading } = useListUserPreferences({
    skip: !rtDoubleOptIn,
  });
  const emailPreference = getPreferenceByType(
    preferencesData?.listUserPreferences,
    PREFERENCE_TYPE_EMAIL_MARKETING
  );

  // Didomi-derived confirmed, never CDS subCommsConfirmed: the preference-centre
  // opt-in never sets the latter, so it reads stale-false (LPB-1425). LPB-1573.
  const alreadyConfirmed = rtDoubleOptIn
    ? Boolean(emailPreference?.enabled && emailPreference?.confirmed === true)
    : Boolean(subCommsConfirmed);

  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState({
    title: doubleOptInPageData?.success_title,
    text: doubleOptInPageData?.success_text,
    button: {
      text: doubleOptInPageData?.success_button_text,
      link: doubleOptInPageData?.success_button_link?.url,
    },
  });

  const showInvalidTicket = () => {
    setContent({
      title: doubleOptInPageData?.error_title,
      text: doubleOptInPageData?.invalid_ticket_error,
      button: {
        text: doubleOptInPageData?.error_button_text,
        link: doubleOptInPageData?.error_button_link?.url,
      },
    });
    setIsLoading(false);
  };

  const [handleConfirmSubComms] = useConfirmSubComms({
    onError: (err: ApolloError) => {
      addErrorInDatadogRum(err);
      // Any non-INVALID_TICKET failure falls through to success — never scare an
      // already-confirmed user with the invalid-ticket screen. LPB-1573.
      if (err.graphQLErrors?.[0]?.errorType === INVALID_TICKET_ERROR_TYPE) {
        showInvalidTicket();
        return;
      }
      setIsLoading(false);
    },
    onCompleted: () => {
      refetchUser && refetchUser();
      setIsLoading(false);
    },
  });

  useEffect(() => {
    if (rtDoubleOptIn && preferencesLoading) return;

    if (alreadyConfirmed) {
      setIsLoading(false);
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const ticket = urlParams.get('ticket');

    if (!ticket) {
      showInvalidTicket();
      return;
    }

    handleConfirmSubComms({ variables: { ticket } });
    // showInvalidTicket closes over page-stable prismic data; omit it to keep this one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyConfirmed, handleConfirmSubComms, rtDoubleOptIn, preferencesLoading]);

  return (
    <Layout
      // TODO: types are not compatible due to the data prop vs prismic types
      data={layout as any}
      omitClose
      lang={lang}
      pathName="/account/confirm-email-preference"
      currentStep={0}
      scripts={[]}
    >
      {isLoading ? (
        <ProcessingMsg loadingMsg={doubleOptInPageData?.loading_message || ''} />
      ) : (
        <DoubleOptIn title={content.title} message={content.text} button={content.button} />
      )}
    </Layout>
  );
};

export default withPrivateRoute(DoubleOptInTemplate, true);

export const query = graphql`
  query ($lang: String) {
    prismicSignUpLayout(lang: { eq: $lang }) {
      _previewable
      data {
        logo_white {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        logo_black {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        background_image {
          alt
          gatsbyImageData
        }
        body {
          ... on PrismicSignUpLayoutDataBodyFooterNavigation {
            items {
              nav_label {
                text
              }
              nav_link {
                text
              }
            }
          }
        }
      }
    }

    prismicDoubleOptInPage(lang: { eq: $lang }) {
      lang
      data {
        loading_message
        success_title
        success_text
        error_title
        invalid_ticket_error
        success_button_text
        success_button_link {
          url
        }
        error_button_text
        error_button_link {
          url
        }
      }
    }
  }
`;
